
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Calendar } from "lucide-react";
import { format, subDays, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type TimeFilter = '7days' | '30days' | '90days' | 'all';

interface KPICardProps {
  title: string;
  value: string;
  trend?: number;
  trendLabel?: string;
  isLoading?: boolean;
}

// Format currency in EUR
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

// Format percentage
const formatPercentage = (value: number) => {
  return new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 2 }).format(value / 100);
};

const KPICard: React.FC<KPICardProps> = ({ title, value, trend = 0, trendLabel, isLoading }) => {
  return (
    <Card className="shadow-sm">
      <CardContent className="p-6">
        {isLoading ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
            <div className="h-8 bg-gray-300 rounded w-32 mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-20"></div>
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <h3 className="text-2xl font-bold mt-1">{value}</h3>
            {trend !== 0 && (
              <div className={`flex items-center mt-2 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? (
                  <TrendingUp className="h-4 w-4 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 mr-1" />
                )}
                <span>{Math.abs(trend)}% {trendLabel}</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const CustomerMetaInsights: React.FC = () => {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30days');
  const [isLoading, setIsLoading] = useState(true);
  const [insights, setInsights] = useState<any[]>([]);
  const { user } = useAuth();
  
  // Metrics
  const [totalSpend, setTotalSpend] = useState(0);
  const [totalImpressions, setTotalImpressions] = useState(0);
  const [totalClicks, setTotalClicks] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [avgCtr, setAvgCtr] = useState(0);
  const [avgCpc, setAvgCpc] = useState(0);
  const [avgCpl, setAvgCpl] = useState(0);
  
  // Calculate date range based on filter
  const getDateRange = () => {
    const today = new Date();
    let startDate;
    
    switch (timeFilter) {
      case '7days':
        startDate = subDays(today, 7);
        break;
      case '30days':
        startDate = subDays(today, 30);
        break;
      case '90days':
        startDate = subDays(today, 90);
        break;
      case 'all':
      default:
        startDate = subMonths(today, 12); // Default to last 12 months for 'all'
    }
    
    return {
      start: format(startDate, 'yyyy-MM-dd'),
      end: format(today, 'yyyy-MM-dd')
    };
  };
  
  // Fetch insights data
  useEffect(() => {
    const fetchInsights = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoading(true);
        const dateRange = getDateRange();
        
        // Fetch insights filtered by customer_id (which is the user's ID) and date range
        const { data, error } = await supabase
          .from('meta_insights')
          .select('*')
          .eq('customer_id', user.id)
          .gte('date_start', dateRange.start)
          .lte('date_stop', dateRange.end)
          .order('date_start', { ascending: false });
        
        if (error) {
          throw error;
        }
        
        if (data) {
          setInsights(data);
          calculateMetrics(data);
        }
      } catch (error) {
        console.error('Error fetching Meta insights:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchInsights();
  }, [timeFilter, user?.id]);
  
  // Calculate metrics from insights data
  const calculateMetrics = (data: any[]) => {
    if (!data || data.length === 0) {
      setTotalSpend(0);
      setTotalImpressions(0);
      setTotalClicks(0);
      setTotalLeads(0);
      setAvgCtr(0);
      setAvgCpc(0);
      setAvgCpl(0);
      return;
    }
    
    // Sum up metrics
    const spend = data.reduce((sum, item) => sum + (item.spend || 0), 0);
    const impressions = data.reduce((sum, item) => sum + (item.impressions || 0), 0);
    const clicks = data.reduce((sum, item) => sum + (item.clicks || 0), 0);
    const leads = data.reduce((sum, item) => sum + (item.leads || 0), 0);
    
    // Calculate averages (weighted by impressions)
    let weightedCtrSum = 0;
    let weightedCpcSum = 0;
    let totalWeight = 0;
    
    data.forEach(item => {
      if (item.impressions > 0) {
        weightedCtrSum += (item.ctr || 0) * item.impressions;
        totalWeight += item.impressions;
      }
      
      if (item.clicks > 0) {
        weightedCpcSum += (item.cpc || 0) * item.clicks;
      }
    });
    
    const ctr = totalWeight > 0 ? weightedCtrSum / totalWeight : 0;
    const cpc = clicks > 0 ? spend / clicks : 0;
    const cpl = leads > 0 ? spend / leads : 0;
    
    // Update state
    setTotalSpend(spend);
    setTotalImpressions(impressions);
    setTotalClicks(clicks);
    setTotalLeads(leads);
    setAvgCtr(ctr);
    setAvgCpc(cpc);
    setAvgCpl(cpl);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Meta Ads Performance</h2>
        <div className="flex items-center">
          <Calendar className="mr-2 h-4 w-4 text-gray-500" />
          <Select value={timeFilter} onValueChange={(value) => setTimeFilter(value as TimeFilter)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Zeitraum wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Letzte 7 Tage</SelectItem>
              <SelectItem value="30days">Letzte 30 Tage</SelectItem>
              <SelectItem value="90days">Letzte 90 Tage</SelectItem>
              <SelectItem value="all">Alle Daten</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Leads"
          value={totalLeads.toString()}
          isLoading={isLoading}
        />
        <KPICard
          title="Ausgaben"
          value={formatCurrency(totalSpend)}
          isLoading={isLoading}
        />
        <KPICard
          title="Kosten pro Lead"
          value={totalLeads > 0 ? formatCurrency(avgCpl) : "—"}
          isLoading={isLoading}
        />
        <KPICard
          title="Klickrate (CTR)"
          value={formatPercentage(avgCtr)}
          isLoading={isLoading}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Kampagnen-Performance</CardTitle>
            <CardDescription>Nach Ausgaben sortiert</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="animate-pulse space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : insights.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Keine Daten verfügbar</p>
            ) : (
              <div className="space-y-2">
                {/* Group by campaign and sum spend */}
                {Array.from(
                  insights.reduce((acc, item) => {
                    const existing = acc.get(item.campaign_id);
                    if (existing) {
                      existing.spend += (item.spend || 0);
                      existing.leads += (item.leads || 0);
                    } else {
                      acc.set(item.campaign_id, {
                        campaign_id: item.campaign_id,
                        campaign_name: item.campaign_name,
                        spend: item.spend || 0,
                        leads: item.leads || 0,
                      });
                    }
                    return acc;
                  }, new Map())
                )
                .map(([_, campaign]) => campaign)
                .sort((a, b) => b.spend - a.spend)
                .slice(0, 5)
                .map((campaign) => (
                  <div key={campaign.campaign_id} className="py-2 border-b border-gray-100 last:border-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium truncate" style={{ maxWidth: '160px' }}>
                        {campaign.campaign_name}
                      </span>
                      <span className="text-right">{formatCurrency(campaign.spend)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{campaign.leads} Leads</span>
                      <span>
                        {campaign.leads > 0 ? formatCurrency(campaign.spend / campaign.leads) : "—"} CPL
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Impressions & Klicks</CardTitle>
            <CardDescription>Übersicht der Reichweite</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Impressions</p>
                    <p className="text-2xl font-bold">
                      {totalImpressions > 999 
                        ? `${(totalImpressions / 1000).toFixed(1)}k` 
                        : totalImpressions}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Klicks</p>
                    <p className="text-2xl font-bold">{totalClicks}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">CTR</p>
                    <p className="text-2xl font-bold">{formatPercentage(avgCtr)}</p>
                  </div>
                </div>
                
                <div className="pt-2">
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span>Kosten pro Klick (CPC)</span>
                    <span className="font-medium">{formatCurrency(avgCpc)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Conversion</CardTitle>
            <CardDescription>Leads und Kosten</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Leads</p>
                    <p className="text-2xl font-bold">{totalLeads}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Kosten/Lead</p>
                    <p className="text-2xl font-bold">{totalLeads > 0 ? formatCurrency(avgCpl) : "—"}</p>
                  </div>
                </div>
                
                <div className="pt-2">
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span>Gesamtausgaben</span>
                    <span className="font-medium">{formatCurrency(totalSpend)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Durchs. CPL</span>
                    <span className="font-medium">{totalLeads > 0 ? formatCurrency(avgCpl) : "—"}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
